// Component: BacktestRunsPage.jsx
// Purpose: Strategy-grouped backtest history with version lineage and fluid animations
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { services } from '../../../../services/api';
import { PageHeader, Segmented, DataTable } from '../../../../components/ds';
import {
  FiActivity, FiClock, FiTrendingUp, FiTrendingDown,
  FiCheckCircle, FiXCircle, FiLoader, FiBarChart2,
  FiTarget, FiCalendar, FiChevronDown, FiChevronLeft,
  FiChevronRight, FiSearch, FiLayers, FiZap
} from 'react-icons/fi';


// --> Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 }
  }
};

const groupVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1, y: 0,
    transition: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }
  }
};

const expandVariants = {
  collapsed: { height: 0, opacity: 0 },
  expanded: {
    height: 'auto', opacity: 1,
    transition: { height: { duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }, opacity: { duration: 0.25, delay: 0.1 } }
  }
};


// --> Status configuration
const STATUS_CONFIG = {
  completed: {
    variant: 'profit', dot: 'bg-[var(--badge-profit-dot)]', icon: <FiCheckCircle className="w-3 h-3" />, label: 'Completed'
  },
  running: {
    variant: 'info', dot: 'bg-[var(--badge-info-dot)]', icon: <FiLoader className="w-3 h-3 animate-spin" />, label: 'Running'
  },
  queued: {
    variant: 'warn', dot: 'bg-[var(--badge-warn-dot)]', icon: <FiClock className="w-3 h-3" />, label: 'Queued'
  },
  failed: {
    variant: 'loss', dot: 'bg-[var(--badge-loss-dot)]', icon: <FiXCircle className="w-3 h-3" />, label: 'Failed'
  },
  cancelled: {
    variant: 'neutral', dot: 'bg-[var(--badge-neut-dot)]', icon: <FiXCircle className="w-3 h-3" />, label: 'Cancelled'
  },
};

const STATUS_FILTERS = ['all', 'completed', 'running', 'failed'];


const BacktestRunsPage = () => {
  const navigate = useNavigate();
  const [runs, setRuns] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const pageSize = 50; // Fetch more to allow grouping

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await services.backtesting.listRuns({ page, page_size: pageSize });
      const data = response.data?.data || response.data;
      setRuns(data?.runs || []);
      setTotal(data?.total || 0);
    } catch (err) {
      console.error('Failed to fetch backtest runs:', err);
      setError('Failed to load backtest history');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { fetchRuns(); }, [fetchRuns]);

  //--> Auto-expand first strategy group on initial load only.
  //    Separated from fetchRuns to avoid stale closure over expandedGroups.
  useEffect(() => {
    if (runs.length > 0 && expandedGroups.size === 0) {
      const firstKey = runs[0]?.strategy_id || runs[0]?.strategy_name || 'unknown';
      setExpandedGroups(new Set([firstKey]));
    }
  }, [runs]); // eslint-disable-line react-hooks/exhaustive-deps

  // --> Group runs by strategy
  const groupedStrategies = useMemo(() => {
    let filtered = runs;

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(r => r.status === statusFilter);
    }

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(r =>
        (r.strategy_name || '').toLowerCase().includes(q) ||
        (r.instrument_id || '').toLowerCase().includes(q) ||
        (r.version_number || '').toLowerCase().includes(q)
      );
    }

    // Group by strategy_id (fallback to strategy_name)
    const groups = {};
    for (const run of filtered) {
      const key = run.strategy_id || run.strategy_name || 'unknown';
      if (!groups[key]) {
        groups[key] = {
          strategyId: run.strategy_id,
          strategyName: run.strategy_name || 'Unknown Strategy',
          instrument: run.instrument_id,
          runs: [],
          bestReturn: null,
          totalRuns: 0,
          versions: new Set(),
        };
      }
      groups[key].runs.push(run);
      groups[key].totalRuns++;
      if (run.version_number) groups[key].versions.add(run.version_number);
      const ret = run.metrics?.total_return_pct;
      if (ret != null && (groups[key].bestReturn == null || ret > groups[key].bestReturn)) {
        groups[key].bestReturn = ret;
      }
    }

    // Sort groups: most recent run first
    return Object.values(groups).sort((a, b) => {
      const aDate = new Date(a.runs[0]?.created_at || 0);
      const bDate = new Date(b.runs[0]?.created_at || 0);
      return bDate - aDate;
    });
  }, [runs, statusFilter, searchQuery]);

  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  };

  const handleViewRun = (run) => {
    const params = new URLSearchParams({ runId: run.run_id, step: 'backtest' });
    navigate(`/strategies/edit/${run.strategy_id}${run.version_id ? `/version/${run.version_id}` : ''}?${params.toString()}`);
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="min-h-screen bg-main-bg dark:bg-main-dark-bg pt-0 px-4 md:px-6 pb-10">
      <div>

        {/* Standardized page header — search + status filter sit in the header
            actions (top-right, inline with the title), matching Strategy Hub. */}
        <PageHeader
          title="Backtest History"
          subtitle={`${total} run${total !== 1 ? 's' : ''} across ${groupedStrategies.length} strateg${groupedStrategies.length !== 1 ? 'ies' : 'y'}`}
          actions={
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full md:w-auto justify-end">
              {/* Search — aligned to the 32px DS control rail (px height; the app's
                  ~13.6px root makes rem-based h-8 render 27px). */}
              <div className="relative w-full md:w-64">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-3" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search strategies, instruments..."
                  className="w-full h-[32px] pl-9 pr-4 bg-bg-2 border border-[var(--divider)] rounded-md text-[13px] text-fg-1 placeholder:text-fg-3 focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                  style={{ '--tw-ring-color': 'var(--theme-purple)' }}
                />
              </div>

              {/* Status Filters — DS Segmented */}
              <Segmented
                aria-label="Status filter"
                value={statusFilter}
                onChange={setStatusFilter}
                options={STATUS_FILTERS.map(sf => ({ value: sf, label: sf.charAt(0).toUpperCase() + sf.slice(1) }))}
              />
            </div>
          }
        />

        {/* ── Error ── */}
        {error && (
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-loss/5 border border-loss/20 rounded-xl p-4 mb-6"
          >
            <p className="text-loss text-sm font-medium">{error}</p>
          </motion.div>
        )}

        {/* ── Loading ── */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-24">
            <div className="w-10 h-10 border-2 border-[var(--divider)] border-t-[var(--theme-purple)] rounded-full animate-spin mb-4" />
            <p className="text-sm text-fg-3">Loading backtest history...</p>
          </div>
        )}

        {/* ── Empty State ── */}
        {!loading && !error && runs.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-24 bg-bg-2 rounded-2xl border border-dashed border-[var(--divider)]"
          >
            <div className="w-16 h-16 rounded-2xl bg-bg-3 flex items-center justify-center mx-auto mb-4">
              <FiBarChart2 className="w-8 h-8 text-fg-3" />
            </div>
            <p className="text-fg-2 text-lg font-bold">No Backtest Runs Yet</p>
            <p className="text-fg-3 text-sm mt-1 max-w-sm mx-auto">
              Open a strategy in the Builder and run a backtest to see results here.
            </p>
            <button
              onClick={() => navigate('/Strategies/Modern')}
              className="mt-6 px-5 h-[32px] inline-flex items-center bg-[var(--theme-purple)] text-white rounded-md text-[13px] font-medium hover:opacity-90 transition-all shadow-sm"
            >
              Go to Strategies
            </button>
          </motion.div>
        )}

        {/* ── No Results After Filter ── */}
        {!loading && !error && runs.length > 0 && groupedStrategies.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16"
          >
            <FiSearch className="w-8 h-8 text-fg-2 mx-auto mb-3" />
            <p className="text-fg-3 text-sm">No runs match your filters</p>
            <button
              onClick={() => { setSearchQuery(''); setStatusFilter('all'); }}
              className="mt-3 text-xs text-[var(--badge-role-fg)] hover:opacity-80 transition-opacity"
            >
              Clear filters
            </button>
          </motion.div>
        )}

        {/* ── Strategy Groups ── */}
        {!loading && groupedStrategies.length > 0 && (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-3"
          >
            {groupedStrategies.map((group) => {
              const groupKey = group.strategyId || group.strategyName;
              const isExpanded = expandedGroups.has(groupKey);
              const versions = Array.from(group.versions).sort().reverse();
              const completedRuns = group.runs.filter(r => r.status === 'completed');
              const avgReturn = completedRuns.length > 0
                ? completedRuns.reduce((s, r) => s + (r.metrics?.total_return_pct || 0), 0) / completedRuns.length
                : null;

              return (
                <motion.div
                  key={groupKey}
                  variants={groupVariants}
                  layout
                  className="bg-bg-2 rounded-xl border border-[var(--divider)] overflow-hidden transition-shadow hover:shadow-md dark:hover:shadow-lg"
                >
                  {/* ── Strategy Group Header ── */}
                  <button
                    onClick={() => toggleGroup(groupKey)}
                    className="w-full flex items-center gap-4 px-5 py-4 text-left group transition-colors hover:bg-bg-3/30"
                  >
                    {/* Expand icon */}
                    <motion.div
                      animate={{ rotate: isExpanded ? 0 : -90 }}
                      transition={{ duration: 0.2 }}
                      className="text-fg-3 group-hover:text-[var(--badge-role-fg)] transition-colors flex-shrink-0"
                    >
                      <FiChevronDown className="w-4 h-4" />
                    </motion.div>

                    {/* Strategy info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2.5">
                        <h3 className="font-semibold text-sm text-fg-1 truncate">
                          {group.strategyName}
                        </h3>
                        <span className="font-mono text-[11px] bg-bg-3 text-fg-2 px-1.5 py-0.5 rounded flex-shrink-0">
                          {group.instrument}
                        </span>
                      </div>
                      {/* Version badges */}
                      <div className="flex items-center gap-1.5 mt-1">
                        {versions.slice(0, 4).map(v => (
                          <span
                            key={v}
                            className="text-[10px] font-mono font-medium px-1.5 py-0.5 rounded bg-[var(--badge-role-bg)] text-[var(--badge-role-fg)] border border-[var(--badge-role-bg)]"
                          >
                            {v}
                          </span>
                        ))}
                        {versions.length > 4 && (
                          <span className="text-[10px] text-fg-2">
                            +{versions.length - 4} more
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Summary metrics */}
                    <div className="hidden sm:flex items-center gap-6 flex-shrink-0">
                      <SummaryMetric
                        label="Runs"
                        value={group.totalRuns}
                        icon={<FiActivity className="w-3 h-3" />}
                      />
                      <SummaryMetric
                        label="Best"
                        value={group.bestReturn != null ? `${group.bestReturn >= 0 ? '+' : ''}${group.bestReturn.toFixed(1)}%` : '—'}
                        color={group.bestReturn != null ? (group.bestReturn >= 0 ? 'text-profit' : 'text-loss') : undefined}
                        icon={<FiZap className="w-3 h-3" />}
                      />
                      <SummaryMetric
                        label="Avg"
                        value={avgReturn != null ? `${avgReturn >= 0 ? '+' : ''}${avgReturn.toFixed(1)}%` : '—'}
                        color={avgReturn != null ? (avgReturn >= 0 ? 'text-profit' : 'text-loss') : undefined}
                        icon={<FiTarget className="w-3 h-3" />}
                      />
                    </div>
                  </button>

                  {/* ── Runs Timeline ── */}
                  <AnimatePresence initial={false}>
                    {isExpanded && (
                      <motion.div
                        key="content"
                        variants={expandVariants}
                        initial="collapsed"
                        animate="expanded"
                        exit="collapsed"
                        className="overflow-hidden"
                      >
                        {/* overflow-x-auto: the 7 nowrap columns exceed a 375px
                            phone, and the animating parent above is overflow-hidden
                            — so the extra columns were CLIPPED and unreachable
                            rather than scrollable. Give the table its own scroller. */}
                        <div className="border-t border-[var(--divider)] overflow-x-auto">
                          {/* Runs table — DS DataTable primitive (roomy rows, matching Orders) */}
                          <DataTable>
                            <DataTable.Head>
                              <DataTable.Row>
                                <DataTable.HeaderCell>Version</DataTable.HeaderCell>
                                <DataTable.HeaderCell align="center">Status</DataTable.HeaderCell>
                                <DataTable.HeaderCell numeric>Return</DataTable.HeaderCell>
                                <DataTable.HeaderCell numeric>Trades</DataTable.HeaderCell>
                                <DataTable.HeaderCell numeric>Win Rate</DataTable.HeaderCell>
                                <DataTable.HeaderCell numeric>Sharpe</DataTable.HeaderCell>
                                <DataTable.HeaderCell align="right">Date Range</DataTable.HeaderCell>
                              </DataTable.Row>
                            </DataTable.Head>
                            <DataTable.Body>
                              {group.runs.map((run) => (
                                <RunRow
                                  key={run.run_id}
                                  run={run}
                                  onClick={() => handleViewRun(run)}
                                />
                              ))}
                            </DataTable.Body>
                          </DataTable>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </motion.div>
        )}

        {/* ── Pagination ── */}
        {!loading && totalPages > 1 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="flex items-center justify-between mt-8 px-1"
          >
            <p className="text-xs text-fg-3 font-mono">
              Page {page} of {totalPages}
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="p-2 rounded-lg hover:bg-bg-3 disabled:opacity-20 disabled:cursor-not-allowed text-fg-2 transition-all"
              >
                <FiChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="p-2 rounded-lg hover:bg-bg-3 disabled:opacity-20 disabled:cursor-not-allowed text-fg-2 transition-all"
              >
                <FiChevronRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};


// --> Summary metric shown in the group header
const SummaryMetric = ({ label, value, color, icon }) => (
  <div className="text-right">
    <div className="flex items-center gap-1 text-[10px] text-fg-3 font-medium justify-end">
      {icon}
      {label}
    </div>
    <p className={`text-sm font-mono font-semibold ${color || 'text-fg-2'}`}>
      {value}
    </p>
  </div>
);


// --> Individual run row inside a strategy group
const RunRow = ({ run, onClick }) => {
  const m = run.metrics || {};
  const totalReturn = m.total_return_pct ?? null;
  const hasReturn = totalReturn != null;
  const isPositive = hasReturn && totalReturn >= 0;
  const sc = STATUS_CONFIG[run.status] || STATUS_CONFIG.cancelled;

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' });
  };

  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return '';
    const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return formatDate(dateStr);
  };

  return (
    <DataTable.Row onClick={onClick} className="cursor-pointer">
      {/* Version + timestamp */}
      <DataTable.Cell>
        <div className="flex items-center gap-2 min-w-0">
          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${sc.dot}`} />
          <span className="font-mono font-medium text-fg-1">{run.version_number || '—'}</span>
          <span className="text-[10px] text-fg-3 whitespace-nowrap">{formatRelativeTime(run.created_at)}</span>
        </div>
      </DataTable.Cell>

      {/* Status */}
      <DataTable.Cell align="center">
        <div className="flex items-center justify-center gap-1">
          <span className={`ds-badge xs ${sc.variant}`}>
            {sc.icon}
            <span className="hidden lg:inline">{sc.label}</span>
          </span>
          {m.degraded && (
            <span className="ds-badge xs warning" title={m.degraded}>DEGRADED</span>
          )}
        </div>
      </DataTable.Cell>

      {/* Return — color on the inner span so it wins over .ds-table td's --fg-1 */}
      <DataTable.Cell numeric>
        <span className={`font-bold ${!hasReturn ? 'text-fg-3' : isPositive ? 'text-profit' : 'text-loss'}`}>
          {hasReturn ? `${isPositive ? '+' : ''}${totalReturn.toFixed(2)}%` : '—'}
        </span>
      </DataTable.Cell>

      {/* Trades */}
      <DataTable.Cell numeric>
        <span className="text-fg-2">{m.total_trades ?? '—'}</span>
      </DataTable.Cell>

      {/* Win Rate */}
      <DataTable.Cell numeric>
        <span className="text-fg-2">{m.win_rate != null ? `${m.win_rate.toFixed(1)}%` : '—'}</span>
      </DataTable.Cell>

      {/* Sharpe */}
      <DataTable.Cell numeric>
        <span className={`font-medium ${m.sharpe_ratio == null ? 'text-fg-3' : m.sharpe_ratio >= 1 ? 'text-profit' : m.sharpe_ratio >= 0 ? 'text-warning' : 'text-loss'}`}>
          {m.sharpe_ratio != null ? m.sharpe_ratio.toFixed(2) : '—'}
        </span>
      </DataTable.Cell>

      {/* Date Range */}
      <DataTable.Cell align="right" className="text-[10px] text-fg-3 whitespace-nowrap">
        {formatDate(run.start_date)} — {formatDate(run.end_date)}
      </DataTable.Cell>
    </DataTable.Row>
  );
};


export default BacktestRunsPage;
